/**
 * Quiz API Service
 * Interface với FastAPI /quiz endpoints
 */

import { apiClient, ApiResponse } from './apiClient';

export interface QuizAnswer {
  question_id: string;
  question_type: string;
  user_answer?: string;
  correct_answer: string;
  is_correct: boolean;
  topic_id?: string;
  difficulty?: string;
}

export interface QuizSubmission {
  quiz_id: string;
  quiz_type: string;
  lesson_id?: string;
  total_questions: number;
  time_spent_seconds: number;
  answers: QuizAnswer[];
}

export interface QuizResult {
  id: number;
  user_id: number;
  quiz_id: string;
  quiz_type: string;
  lesson_id?: string;
  total_questions: number;
  correct_answers: number;
  score_percentage: number;
  time_spent_seconds: number;
  ai_analysis?: string;
  weak_topics?: string[];
  strong_topics?: string[];
  submitted_at: string;
  analyzed_at?: string;
}

export interface QuizStats {
  total_quizzes: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  total_questions_answered: number;
  total_correct: number;
}

export interface QuestionTypeStats {
  count: number;
  exists: boolean;
  relativePath?: string;
  dbPath?: string;
}

export interface BankQuizItem {
  quizUrl: string;
  title: string;
  description: string;
  lop: number;
  chuong: number;
  bai: number;
  totalQuestions: number;
  questionTypes: Record<string, QuestionTypeStats>;
}

export interface ScanResult {
  stats: {
    totalFiles: number;
    totalQuizzes: number;
  };
  quizzes: BankQuizItem[];
}

export interface QuestionCounts {
  TN: number;
  DS: number;
  TL: number;
  M1: number;
  M2: number;
  M3: number;
}

class QuizApi {
  /**
   * Submit quiz for grading
   */
  async submitQuiz(data: QuizSubmission): Promise<ApiResponse<QuizResult>> {
    return apiClient.post<QuizResult>('/quiz/submit', data);
  }

  /**
   * Get quiz history
   */
  async getHistory(limit: number = 20): Promise<ApiResponse<QuizResult[]>> {
    return apiClient.get<QuizResult[]>(`/quiz/history?limit=${limit}`);
  }

  /**
   * Get quiz statistics (user specific)
   */
  async getStats(): Promise<ApiResponse<QuizStats>> {
    return apiClient.get<QuizStats>('/quiz/stats');
  }

  /**
   * Get leaderboard
   */
  async getLeaderboard(): Promise<ApiResponse<any>> {
    return apiClient.get('/quiz/leaderboard');
  }

  /**
   * Scan for all available quizzes (Bank)
   */
  async scanQuizzes(): Promise<ScanResult> {
    const res = await apiClient.get<ScanResult>('/quiz/scan', false); // No auth required for public bank
    if (res.data) return res.data;
    return { stats: { totalFiles: 0, totalQuizzes: 0 }, quizzes: [] };
  }

  /**
   * Get question counts for a specific lesson
   */
  async getQuestionCounts(lop: number, chuong: number, bai: number): Promise<QuestionCounts> {
    const res = await apiClient.get<QuestionCounts>(`/quiz/counts?lop=${lop}&chuong=${chuong}&bai=${bai}`, false);
    if (res.data) return res.data;
    return { TN: 0, DS: 0, TL: 0, M1: 0, M2: 0, M3: 0 };
  }
}

export const quizApi = new QuizApi();

